/**
 * The "stream never started" safety timer of the composer.
 *
 * When a search is sent (`enterPressed`), the composer shows its spinner and
 * waits for the search to answer. If nothing at all comes back, the spinner
 * must not run forever: after STREAM_START_TIMEOUT_MS the bar hands the
 * composer back and reports `stream-never-started`.
 *
 * "Something came back" used to mean one thing only: a message in the
 * conversation. That is right for a category or kit search, whose first
 * answer is a streamed turn. A named-product search ("Silva Ex10", "Garmin
 * Forerunner 975") never streams a turn: classify-intent routes it to the
 * product fast path, which resolves the name, looks the page up, then builds
 * the search map, a minute or more with the conversation still empty. The
 * composer stays in its sent state the whole time (search-bar.tsx clears
 * `enterPressed` only once that branch returns), so the timer fired on every
 * such search at 15 s: the spinner vanished mid-search, a false
 * `stream-never-started` alert went out, and the shopper typed the query again
 * (search QA 2026-10-02, item 2).
 *
 * The product fast path has its own signs of life, and the bar already
 * receives them: the resolver's streamed "browsing" status (`browsingLabel`),
 * the page lookup (`isCheckingAlgoliaForRedirect`, which has its own timeout
 * and degraded path) and the map build (`isProductBranchLoading`; a failed
 * build cancels the search itself). While one of them is on, the stream has
 * started as far as this timer is concerned, so it does not run. Any change of
 * the signs is an answer of sorts, so the wait starts over from there; only
 * STREAM_START_TIMEOUT_MS of complete silence fires it.
 */
import { useEffect, useRef } from 'react';

export const STREAM_START_TIMEOUT_MS = 15_000;

/** What the bar can see of a search before its conversation has a message. */
export interface StreamStartSigns {
  /** A search was sent and the composer is waiting on it. */
  enterPressed: boolean;
  /** Messages in the conversation; the first one means a turn streamed. */
  messageCount: number;
  /** classify-intent is deciding where the query goes (a restart point, not a pause). */
  isClassifyingIntent?: boolean;
  /** The product resolver streamed its "browsing" status. */
  browsing?: boolean;
  /** The product fast path is looking the page up. */
  isCheckingAlgoliaForRedirect?: boolean;
  /** The product fast path is building the search map. */
  isProductBranchLoading?: boolean;
}

/**
 * True while nothing has come back for the sent search: the only state the
 * timer counts in.
 */
export function awaitingStreamStart(signs: StreamStartSigns): boolean {
  return (
    signs.enterPressed &&
    signs.messageCount === 0 &&
    !signs.browsing &&
    !signs.isCheckingAlgoliaForRedirect &&
    !signs.isProductBranchLoading
  );
}

export interface StreamStartWatch {
  /** Feed every change of the signs. Arms, restarts or disarms the timer. */
  update(signs: StreamStartSigns): void;
  /** Disarm for good (unmount). */
  stop(): void;
}

/**
 * The timer itself, with no React in it so a test can drive a whole search
 * through it under fake timers.
 */
export function createStreamStartWatch(
  onTimeout: () => void,
  timeoutMs: number = STREAM_START_TIMEOUT_MS,
): StreamStartWatch {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return {
    update(signs) {
      clear();
      if (!awaitingStreamStart(signs)) return;
      timer = setTimeout(() => {
        timer = null;
        onTimeout();
      }, timeoutMs);
    },
    stop: clear,
  };
}

/**
 * Run the watch for the life of the component. `onTimeout` is read at fire
 * time, so a parent that passes a new function on every render does not
 * restart the wait.
 */
export function useStreamStartTimeout(signs: StreamStartSigns, onTimeout: () => void): void {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const watchRef = useRef<StreamStartWatch | null>(null);
  if (watchRef.current === null) {
    watchRef.current = createStreamStartWatch(() => onTimeoutRef.current());
  }

  const {
    enterPressed,
    messageCount,
    isClassifyingIntent = false,
    browsing = false,
    isCheckingAlgoliaForRedirect = false,
    isProductBranchLoading = false,
  } = signs;
  useEffect(() => {
    watchRef.current?.update({
      enterPressed,
      messageCount,
      isClassifyingIntent,
      browsing,
      isCheckingAlgoliaForRedirect,
      isProductBranchLoading,
    });
  }, [enterPressed, messageCount, isClassifyingIntent, browsing, isCheckingAlgoliaForRedirect, isProductBranchLoading]);

  useEffect(() => () => watchRef.current?.stop(), []);
}
